import MidiPlayer from "midi-player-js";
import { TimeMapEntry } from "@/models/TimeMapEntry";
import { SplendidGrandPiano } from "smplr";

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

export const useMidiStore = defineStore("midi", () => {
  let player: MidiPlayer.Player | null = null;
  let instrument: SplendidGrandPiano | null = null;

  const debug = useDebug();
  const mediaStore = useMediaPlayerStore();
  const fs = useFullScreenStore();
  const defaultBpm = ref(0);
  const ready = ref(false);
  const bpm = ref(0);
  const bpmFactor = ref(1);
  let timemap: any[] = [];
  const playing = ref(false);
  const duration = ref(0);
  const progressTime = ref(0);
  const autoScroll = ref(false);
  const fullMidiController = ref(false);
  const route = useRoute();
  let rawMidi: string = "";
  const progressPercent = computed(
    () => `${(progressTime.value / duration.value) * 100}%`,
  );
  const formattedDuration = computed(() => formatSeconds(duration.value));
  const formattedProgressTime = computed(() =>
    formatSeconds(progressTime.value),
  );
  let initTick = 0;
  let lastTick = -1;

  function formatSeconds(seconds: number) {
    return new Date(seconds * 1000).toISOString().substr(14, 5);
  }

  async function loadInstrument() {
    if (instrument) {
      return;
    }

    //instrument = await Soundfont.instrument(ac, "acoustic_grand_piano");
    if (!window.AudioContext) {
      window.AudioContext = window.webkitAudioContext;
    }
    const ac = new window.AudioContext();
    instrument = new SplendidGrandPiano(ac, {
      decayTime: 1,
      baseUrl:
        "https://cdn.ccli.com/songselect/midi-samples/splendid-grand-piano",
    });
    (window as any).instrument = instrument;
  }

  async function init() {
    if (player) {
      return;
    }

    if (!instrument) {
      throw new Error("instrument not found in init");
    }

    await instrument.loaded();

    player = new MidiPlayer.Player(async (e: any) => {
      if (!player) {
        throw new Error("player not found");
      }
      if (!instrument) {
        throw new Error("instrument not available");
      }

      if (playing.value) {
        progressTime.value = duration.value - player.getSongTimeRemaining();
      }

      if (e.name === "Note on" && e.velocity !== 0) {
        debug.log("playing note", e, instrument);
        instrument.start({
          note: e.noteName,
          velocity: e.velocity,
          duration: 2,
        });
      }

      if (bpm.value !== player.tempo) {
        setBpm(bpm.value);
      }
    });

    player.on("endOfFile", async (e: any) => {
      lastTick = -1;
      // app.currentPage = 1;
      removeHighlights();
      await nextTick();
      playing.value = false;
      progressTime.value = 0;
    });

    player.on("fileLoaded", (e: any) => {
      initTick = e.startTick;
    });

    player.on("playing", (e: any) => {
      updateVisualAtTick(e.tick);
    });

    (window as any).player = player;
  }

  function updateVisualAtTick(tick: number) {
    if (!player) {
      return;
    }

    const qstampStart = lastTick / player.division;
    const qstampEnd = tick / player.division;

    const notesFound = timemap.filter(
      (t) => t.qstamp > qstampStart && t.qstamp <= qstampEnd,
    );

    if (notesFound.length) {
      removeHighlights();
      notesFound.forEach((n) => {
        if (n.measureOn) {
          drawRedLine(n.measureOn);
        }
        if (n.on) {
          highlightNotes(n.on);
        }
        if (n.off) {
          unhighlightNotes(n.off);
        }
        if (n.restsOn) {
          highlightNotes(n.restsOn);
        }
        if (n.restsOff) {
          unhighlightNotes(n.restsOff);
        }
      });
    }
    lastTick = tick;
  }

  const redLine = document.createElement("div");
  redLine.style.background = "red";
  redLine.style.width = "2px";
  redLine.style.position = "fixed";
  redLine.style.zIndex = "10";
  redLine.style.transition = "0.1s all ease";
  redLine.style.left = "10px";
  redLine.style.top = "10px";
  redLine.style.height = "100px";
  redLine.style.display = "none";

  document.body.appendChild(redLine);

  function drawRedLine(measureId: string) {
    const element = document.querySelector(`#${measureId}`);
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    redLine.style.height = rect.height + "px";
    redLine.style.top = rect.y + "px";
    redLine.style.left = rect.x + "px";
  }

  let lastPlayingPageElement: null | Element = null;

  function highlightNotes(elementIds: string[]) {
    if (fs.fullScreenEnabled) {
      const ids = elementIds.map((id: string) => `#${id}`).join(",");
      const elements = document.querySelectorAll(ids);
      if (elements && elements.length) {
        elements.forEach((e) => {
          e.classList.add("highlight-active");
          const system = e.closest(".system");
          if (autoScroll.value) {
            system?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }

          const page = e.closest(".musicpage");

          if (
            lastPlayingPageElement === null ||
            lastPlayingPageElement !== page
          ) {
            const wrapper = e.closest(".wrapper");

            if (page && wrapper) {
              const scrollTop =
                (page as HTMLDivElement).offsetTop -
                (wrapper as HTMLDivElement).offsetTop;
              wrapper.scrollTo({ top: scrollTop, behavior: "smooth" });
            }

            lastPlayingPageElement = page;
          }
        });
      }
    } else {
      elementIds.forEach((id: string) => {
        const element = document.getElementById(id);
        if (element) {
          element.classList.add("highlight-active");

          const system = element.closest(".system") as HTMLElement | null;
          if (autoScroll.value && system) {
            const systemRect = system.getBoundingClientRect();
            const scrollPosition =
              window.scrollY +
              systemRect.top -
              (window.innerHeight - systemRect.height) / 2;

            window.scrollTo({
              top: scrollPosition,
              behavior: "smooth",
            });
          }

          const page = element.closest(".musicpage") as HTMLElement | null;

          if (
            lastPlayingPageElement === null ||
            lastPlayingPageElement !== page
          ) {
            const wrapper = element.closest(".wrapper") as HTMLElement | null;

            if (page && wrapper) {
              const pageRect = page.getBoundingClientRect();
              const wrapperRect = wrapper.getBoundingClientRect();
              const scrollTop =
                wrapper.scrollTop + pageRect.top - wrapperRect.top;

              wrapper.scrollTo({ top: scrollTop, behavior: "smooth" });
            }

            lastPlayingPageElement = page;
          }
        }
      });
    }
  }

  function unhighlightNotes(elementIds: string[]) {
    const ids = elementIds.map((id: string) => `#${id}`).join(",");
    const elements = document.querySelectorAll(ids);
    if (elements && elements.length) {
      elements.forEach((e) => e.classList.remove("highlight-active"));
    }
  }

  function removeHighlights() {
    document
      .querySelectorAll("svg .highlight-active")
      .forEach((e) => e.classList.remove("highlight-active"));
  }

  function pause() {
    removeHighlights();
    player?.pause();
    instrument?.stop();
    lastPlayingPageElement = null;
  }

  function play() {
    instrument?.context.resume();
    if (!ready.value) {
      loadSong();
      return;
    }
    player?.play();
    mediaStore.pause();
    playing.value = true;
    lastPlayingPageElement = null;
  }

  function stop() {
    removeHighlights();
    instrument?.stop();
    player?.stop();
    playing.value = false;
    lastPlayingPageElement = null;
  }

  async function togglePlay() {
    instrument?.context.resume();

    if (!ready.value) {
      await loadSong();
      return;
    }

    playing.value = !playing.value;
    if (playing.value) {
      mediaStore.pause();
      player?.play();
    } else {
      instrument?.stop();
      player?.pause();
    }
  }

  function skipToSeconds(seconds: number) {
    if (!player) {
      return;
    }
    player.skipToSeconds(seconds);
    play();
  }

  // should be called before loading song
  async function preloadDataForPlayback(
    midiData: string,
    _timemap: TimeMapEntry[],
    _defaultBpm: number | null,
  ) {
    clearSong();
    rawMidi = midiData;
    timemap = _timemap;
    defaultBpm.value = _defaultBpm || 120;
    bpm.value = defaultBpm.value || 120;

    if (timemap[0].tempo) {
      bpmFactor.value = defaultBpm.value / parseInt(timemap[0].tempo);
    } else {
      bpmFactor.value = 1;
    }
  }

  async function loadSong() {
    lastPlayingPageElement = null;
    if (!rawMidi) {
      throw new Error("no midi found");
    }
    if (!timemap) {
      throw new Error("no timemap file found");
    }
    if (!defaultBpm.value) {
      defaultBpm.value = 120;
    }

    await init();

    if (!player) {
      throw new Error("Midi player not initialized");
    }

    ready.value = false;
    lastTick = -1;
    player.loadDataUri("data:audio/midi;base64," + rawMidi);

    duration.value = player.getSongTime() || 0;
    progressTime.value = 0;
    ready.value = true;
    await play();
    player.tempo = Math.round(bpm.value / bpmFactor.value);
  }

  //for now just assume this is clicked during playback.
  async function noteclick(noteId: string) {
    const entryIndex = timemap.findIndex(
      (i: any) => i.on && i.on.includes(noteId),
    );

    if (entryIndex < 0) {
      throw new Error("cannot find noteId in timemap");
    }

    if (!ready.value) {
      await loadSong();
    }
    if (!player) {
      throw new Error("Cannot find midi player");
    }

    lastTick = -1;
    if (entryIndex >= 1) {
      lastTick = timemap[entryIndex - 1].qstamp * player.division;
    }

    const qstamp = timemap[entryIndex].qstamp;
    const tick = qstamp * player.division;
    player.skipToTick(tick);
    player.play();
    playing.value = true;
  }

  function clearSong() {
    ready.value = false;
    stop();
    player = null;
  }

  function restartSong() {
    stop();
    loadSong();
  }

  function toggleAutoScroll() {
    autoScroll.value = !autoScroll.value;
  }

  function setAutoScroll(v: boolean) {
    autoScroll.value = v;
  }

  function setFullMidiController(v: boolean) {
    fullMidiController.value = v;
  }

  watch(
    () => route.fullPath,
    () => {
      clearSong();
      setFullMidiController(false);
    },
  );

  async function setBpm(_bpm: number) {
    if (!player) {
      return;
    }
    const wasPlaying = player.isPlaying();
    if (wasPlaying) {
      player.pause();
    }

    bpm.value = _bpm;
    player.tempo = Math.round(bpm.value / bpmFactor.value);
    duration.value = player.getSongTime() || 0;
    await nextTick();

    if (wasPlaying) {
      player.play();
    }
  }

  return {
    ready,
    bpm,
    playing,
    duration,
    progressTime,
    pause,
    play,
    skipToSeconds,
    init,
    stop,
    togglePlay,
    setBpm,
    loadSong,
    noteclick,
    clearSong,
    restartSong,
    autoScroll,
    toggleAutoScroll,
    setAutoScroll,
    fullMidiController,
    setFullMidiController,
    progressPercent,
    formattedDuration,
    formattedProgressTime,
    timemap,
    preloadDataForPlayback,
    loadInstrument,
  };
});
